import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { GasReading } from "@/api/queries/incidents";

type Row = { ts: string } & Record<string, number | string | null | undefined>;

export function ExposureChart({
  readings,
  tz,
}: {
  readings: GasReading[];
  tz?: string;
}) {
  if (!readings.length) {
    return (
      <div className="flex h-72 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        No gas readings recorded for this incident.
      </div>
    );
  }
  // Pivot to one row per timestamp, one column per gas.
  const byTs = new Map<string, Row>();
  const gases = new Set<string>();
  for (const r of readings) {
    gases.add(r.gas);
    const existing = byTs.get(r.measured_at) ?? { ts: r.measured_at };
    existing[r.gas] = r.value_ppm;
    byTs.set(r.measured_at, existing);
  }
  const data = Array.from(byTs.values()).sort((a, b) =>
    String(a.ts).localeCompare(String(b.ts)),
  );
  /*
   * Series encoding, brand palette (Visual Identity Guide v1.0).
   *
   * The brand has two chromatic families (red + blue); status colours are
   * reserved for exposure state and must never stand in as a "series 3".
   * Validated: red-500 vs blue-500 separate at ΔE 35.3 normal-vision / 29.9
   * worst-case colour-vision-deficiency, in both light and dark. Any third
   * *hue* drawn from these ramps lands below the ΔE 15 legibility floor, so
   * hue alone cannot carry more than two series.
   *
   * Series beyond the second are therefore distinguished by stroke pattern —
   * secondary encoding rather than an off-brand hue. Colour cycles every 2,
   * dash changes every 2, giving 6 unambiguous combinations. The legend
   * reproduces both, so identity is never colour-alone.
   */
  const palette = ["#D5312C", "#4C8DFF"];
  const dashes = [undefined, undefined, "6 4", "6 4", "2 3", "2 3"];
  const gasList = Array.from(gases);
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{ top: 12, right: 24, bottom: 12, left: 0 }}
        >
          {/* Recessive grid, themed via the border token. Set as a class rather
              than the stroke prop: Recharts emits stroke as an SVG presentation
              attribute (where var() does not resolve), and a CSS declaration
              outranks a presentation attribute, so this follows light/dark. */}
          <CartesianGrid className="stroke-border" strokeDasharray="3 3" />
          <XAxis
            dataKey="ts"
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => {
              try {
                const d = new Date(String(v));
                return tz
                  ? d.toLocaleTimeString("en-CA", {
                      timeZone: tz,
                      hour12: false,
                    })
                  : d.toLocaleTimeString("en-CA", { hour12: false });
              } catch {
                return String(v);
              }
            }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            label={{
              value: "ppm",
              angle: -90,
              position: "insideLeft",
              fontSize: 11,
            }}
          />
          <Tooltip />
          <Legend />
          {gasList.map((gas, idx) => (
            <Line
              key={gas}
              type="monotone"
              dataKey={gas}
              stroke={palette[idx % palette.length]}
              strokeDasharray={dashes[idx % dashes.length]}
              dot={false}
              strokeWidth={2}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
